import React from "react";
import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "microeconomics-sem-1-unit-6-monopoly-price-discrimination-deadweight-loss",
  title: "Introductory Microeconomics Unit 6: Monopoly, Profit Maximization & Price Discrimination Notes",
  description: "Complete DU Sem 1 Unit 6 notes: Monopoly equilibrium, MR-elasticity relation, absence of supply curve, Lerner Index, Deadweight Loss & 1st, 2nd, 3rd Degree Price Discrimination.",
  pubDate: "2025-08-22",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["Monopoly", "Price Discrimination", "Lerner Index", "Deadweight Loss", "Market Power", "DU Economics"],
  readMinutes: 17,
  heroEyebrow: "Sem 1 · Unit 6 Notes"
};

export function Body() {
  return (
    <>
      <p>
        For undergraduate economics students at Delhi University, <strong>Unit 6: Monopoly and Price Discrimination</strong> marks a pivotal transition. After spending weeks mastering the theoretical utopia of perfect competition, you are now introduced to the strategic realities of market power. While perfect competition assumes firms are price-takers, this unit introduces the <strong>price-maker</strong>. 
      </p>

      <p>
        A monopoly is a market structure characterized by a single seller of a unique product with no close substitutes, protected by significant barriers to entry. In this comprehensive 3,000+ word study guide, we will unpack the core mechanics of monopoly behavior, the welfare implications (deadweight loss), and the fascinating mathematics of price discrimination. We have also included <strong>eight fully solved numerical problems</strong> mapping directly to the 15-mark DU examination format.
      </p>

      <hr />

      <h2>1. Monopoly Sources &amp; Characteristics</h2>
      <p>
        Why do monopolies exist? The defining feature of a monopoly is <strong>barriers to entry</strong>. Without these barriers, positive economic profits would attract new entrants, eroding the monopolist's market power. The primary sources of monopoly power include:
      </p>
      <ul>
        <li><strong>Natural Monopoly:</strong> Arises when economies of scale are so pervasive that a single firm can supply the entire market at a lower Average Total Cost (ATC) than two or more firms. Examples include local utility companies (water, electricity grids).</li>
        <li><strong>Patents and Copyrights:</strong> Government-granted exclusive rights to produce or sell an invention, incentivizing innovation (e.g., pharmaceutical drugs).</li>
        <li><strong>Network Externalities:</strong> A situation where the value of a product increases as more people use it, naturally leading to market dominance (e.g., social media platforms, operating systems).</li>
        <li><strong>Control of Raw Materials:</strong> Exclusive ownership of a scarce resource (e.g., De Beers' historical control over the global diamond supply).</li>
      </ul>

      <h3>Key Characteristics</h3>
      <p>
        Unlike a perfectly competitive firm facing a perfectly elastic (horizontal) demand curve, a monopolist faces the downward-sloping <strong>market demand curve</strong>. This fundamentally alters their revenue structure. They cannot simply sell infinite quantities at the going market price; to sell more, they must lower the price.
      </p>

      <hr />

      <h2>2. Average &amp; Marginal Revenue under Monopoly</h2>
      <p>
        For a monopolist, Average Revenue (AR) is simply the price of the good (AR = TR/Q = P(Q)). Since the demand curve is downward sloping, the AR curve is identical to the market demand curve.
      </p>
      
      <h3>Proof that MR &lt; P</h3>
      <p>
        Marginal Revenue (MR) is the addition to total revenue from selling one more unit. Because the monopolist must lower the price on <em>all</em> units to sell an additional unit, MR is always less than the price (except at the very first unit).
      </p>
      <p>
        Let Demand be P = a - bQ.<br/>
        Total Revenue (TR) = P * Q = (a - bQ)Q = aQ - bQ^2.<br/>
        Marginal Revenue (MR) = d(TR)/dQ = a - 2bQ.<br/>
        Notice that the MR curve has the same price intercept (a) but twice the slope (-2b) of the demand curve. Therefore, for any Q &gt; 0, MR &lt; P.
      </p>

      <h3>Derivation of MR = P(1 - 1/|e|)</h3>
      <p>
        One of the most important formulas in microeconomics links Marginal Revenue to the price elasticity of demand (e). 
      </p>
      <p>
        TR = P(Q) * Q <br/>
        Using the product rule: MR = d(TR)/dQ = P + Q * (dP/dQ)<br/>
        Factor out P: MR = P [1 + (Q/P) * (dP/dQ)]<br/>
        Recall that the price elasticity of demand is e = (dQ/dP) * (P/Q). Therefore, the reciprocal is 1/e = (dP/dQ) * (Q/P).<br/>
        Substitute this back: MR = P [1 + 1/e].<br/>
        Since demand elasticity (e) is negative, we can use absolute value |e|: <strong>MR = P(1 - 1/|e|)</strong>.
      </p>

      <h3>Why a Monopolist Never Operates on the Inelastic Portion of Demand</h3>
      <p>
        Examine the formula MR = P(1 - 1/|e|):
      </p>
      <ul>
        <li>If demand is elastic (|e| &gt; 1), 1/|e| &lt; 1, so MR &gt; 0.</li>
        <li>If demand is unit elastic (|e| = 1), 1/|e| = 1, so MR = 0.</li>
        <li>If demand is inelastic (|e| &lt; 1), 1/|e| &gt; 1, so <strong>MR &lt; 0</strong>.</li>
      </ul>
      <p>
        A profit-maximizing monopolist sets MR = MC. Since Marginal Cost (MC) must be positive, MR must also be positive. Therefore, a monopolist will <em>always</em> price on the elastic portion of the demand curve (|e| &gt; 1). If they were on the inelastic portion, they could raise the price, which would reduce quantity, reduce total costs, but actually <em>increase</em> total revenue, strictly increasing profit.
      </p>

      <hr />

      <h2>3. Short-Run and Long-Run Monopoly Equilibrium</h2>
      
      <h3>First-Order and Second-Order Conditions (FOC &amp; SOC)</h3>
      <p>
        The objective of the monopolist is to maximize profit (Pi).<br/>
        Pi = TR(Q) - TC(Q)
      </p>
      <p>
        <strong>First-Order Condition (FOC):</strong> Set the first derivative to zero.<br/>
        d(Pi)/dQ = d(TR)/dQ - d(TC)/dQ = 0<br/>
        MR - MC = 0  =&gt;  <strong>MR = MC</strong>
      </p>
      <p>
        <strong>Second-Order Condition (SOC):</strong> The second derivative must be negative for a maximum.<br/>
        d^2(Pi)/dQ^2 &lt; 0<br/>
        d(MR)/dQ - d(MC)/dQ &lt; 0<br/>
        Slope of MR &lt; Slope of MC. This means the MC curve must cut the MR curve from below.
      </p>
      
      <h3>The Absence of a Unique Supply Curve</h3>
      <p>
        A frequent DU exam question asks: <em>"Why does a monopoly not have a supply curve?"</em>
      </p>
      <p>
        In perfect competition, the supply curve maps Price to Quantity Supplied (because P = MC). A monopolist, however, does not take price as given. Instead, they choose output where MR = MC, and then read the price off the demand curve. Depending on the shape and elasticity of the demand curve, a monopolist could supply:
      </p>
      <ul>
        <li>The same quantity at two different prices (if demand curves pivot).</li>
        <li>Different quantities at the exact same price (if demand curves shift in specific ways).</li>
      </ul>
      <p>
        Because there is no one-to-one relationship between Price and Quantity Supplied, a monopoly has no unique supply curve.
      </p>

      <hr />

      <h2>4. Measurement of Monopoly Power</h2>
      <p>
        Monopoly power is the ability of a firm to price above marginal cost. The two standard metrics are:
      </p>

      <h3>1. The Lerner Index (L)</h3>
      <p>
        The Lerner Index directly measures the markup of price over marginal cost.<br/>
        <strong>L = (P - MC) / P</strong>
      </p>
      <p>
        We can relate this to elasticity using the MR = MC equilibrium condition.<br/>
        Since MR = P(1 - 1/|e|) and MR = MC:<br/>
        MC = P - P/|e|<br/>
        P - MC = P/|e|<br/>
        (P - MC) / P = 1 / |e|<br/>
        <strong>Therefore, L = 1 / |e|.</strong>
      </p>
      <p>
        The index ranges from 0 to 1. In perfect competition, |e| is infinite, L = 0 (no market power). As demand becomes less elastic, L approaches 1, indicating high monopoly power.
      </p>

      <h3>2. Herfindahl-Hirschman Index (HHI)</h3>
      <p>
        Used by regulators to measure market concentration, the HHI is the sum of the squares of the market shares (s) of all firms in the industry.<br/>
        HHI = s1^2 + s2^2 + ... + sn^2.<br/>
        For a pure monopoly, s1 = 100, so HHI = 100^2 = 10,000.
      </p>

      <hr />

      <h2>5. Welfare Analysis &amp; Social Cost of Monopoly</h2>
      
      <h3>The Deadweight Loss (DWL) Triangle</h3>
      <p>
        Monopolies are inefficient compared to perfectly competitive markets. A competitive market produces where P = MC, maximizing total economic surplus. A monopolist restricts output (Qm &lt; Qc) to raise the price (Pm &gt; Pc).
      </p>
      <p>
        Because P &gt; MC for the units between Qm and Qc, consumers value these goods more than it costs to produce them. By not producing these units, the monopolist creates a <strong>Deadweight Loss (DWL)</strong>—a permanent loss of social welfare. On a graph, this is the triangle bounded by the Demand curve, the MC curve, and the monopoly output quantity Qm.
      </p>

      <h3>Rent-Seeking Expenditures</h3>
      <p>
        The true social cost of a monopoly is often higher than just the DWL triangle. Monopolists may spend vast resources (lobbying, legal fees, massive advertising) to acquire or maintain their monopoly status. These unproductive expenses are known as <strong>rent-seeking expenditures</strong>, further reducing total societal welfare.
      </p>

      <hr />

      <h2>6. Price Discrimination</h2>
      <p>
        Price discrimination occurs when a firm charges different prices to different consumers for the same good, where price differences are not justified by cost differences. 
      </p>
      <p><strong>Required Conditions:</strong></p>
      <ol>
        <li>The firm must have market power (price-making ability).</li>
        <li>The firm must be able to segment the market based on willingness to pay.</li>
        <li>There must be no arbitrage (consumers buying at a low price cannot resell to high-price consumers).</li>
      </ol>

      <h3>First-Degree (Perfect) Price Discrimination</h3>
      <p>
        The monopolist charges every consumer their exact maximum willingness to pay (reservation price) for each unit. 
        <strong>Welfare Impact:</strong> The monopolist extracts 100% of the Consumer Surplus. However, because they produce every unit where P &gt;= MC, output equals the competitive output (Qc). <strong>Deadweight Loss is ZERO.</strong> It is perfectly efficient, but perfectly inequitable.
      </p>

      <h3>Second-Degree Price Discrimination (Block Pricing)</h3>
      <p>
        The firm cannot identify individual willingness to pay, so it offers pricing menus or quantity discounts, forcing consumers to self-select. Example: Electricity blocks (first 100 units at Rs. 4/unit, next 100 at Rs. 3/unit).
      </p>

      <h3>Third-Degree Price Discrimination (Market Segmentation)</h3>
      <p>
        The most common form. The firm separates consumers into distinct markets (e.g., student vs. adult movie tickets) with different demand elasticities.
        <br/><strong>Optimization Rule:</strong> The firm maximizes profit by equating the Marginal Revenue in each market to the overall Marginal Cost.
        <br/><strong>MR1 = MR2 = MC</strong>
      </p>
      <p>
        Since MR1 = P1(1 - 1/|e1|) and MR2 = P2(1 - 1/|e2|), equating them gives the pricing formula:
        <br/><strong>P1 / P2 = (1 - 1/|e2|) / (1 - 1/|e1|)</strong>
        <br/><em>Rule of thumb:</em> The sub-market with the more inelastic demand (lower |e|) will be charged the higher price.
      </p>

      <h3>Peak-Load Pricing</h3>
      <p>
        Distinct from 3rd-degree discrimination because marginal costs actually vary. Demand is higher during peak times (e.g., rush hour metro, daytime electricity). The firm charges higher prices during peak times because MC is higher (pushing against capacity constraints) and demand is more inelastic.
      </p>

      <hr />

      <h2>7. Natural Monopoly Regulation</h2>
      <p>
        A natural monopoly has continuously declining Average Total Cost (ATC) due to massive fixed costs. Marginal Cost (MC) is always below ATC.
      </p>
      <ul>
        <li><strong>Marginal Cost Pricing (P = MC):</strong> Regulators force the firm to price at MC to achieve allocative efficiency (zero DWL). However, since MC &lt; ATC, the firm makes an economic loss and will exit the market unless the government provides a <strong>subsidy</strong> equal to the loss.</li>
        <li><strong>Average Cost Pricing (P = ATC):</strong> The regulator sets price where the demand curve intersects the ATC curve. The firm earns exactly zero economic profit (a "fair rate of return"). There is some deadweight loss, but no government subsidy is required. This is the most common regulatory compromise.</li>
      </ul>

      <hr />

      <h2>8. Fully Solved Numerical Problems (DU Exam Focus)</h2>
      <p>
        The backbone of DU Microeconomics exams is the numerical section. Let's walk through 8 rigorous examples.
      </p>

      <h3>Problem 1: Standard Monopoly Equilibrium</h3>
      <p>
        <strong>Q:</strong> A monopolist faces demand P = 100 - 2Q. Their total cost is TC = Q^2 + 20Q + 50. Find profit-maximizing Price, Quantity, and Profit.<br/>
        <strong>Solution:</strong><br/>
        1. Find MR: TR = (100 - 2Q)Q = 100Q - 2Q^2. MR = d(TR)/dQ = 100 - 4Q.<br/>
        2. Find MC: MC = d(TC)/dQ = 2Q + 20.<br/>
        3. Equate MR = MC: 100 - 4Q = 2Q + 20  =&gt;  80 = 6Q  =&gt;  Q = 13.33 units.<br/>
        4. Find Price: P = 100 - 2(13.33) = 100 - 26.67 = Rs. 73.33.<br/>
        5. Find Profit: TR = 73.33 * 13.33 = 977.77. TC = (13.33)^2 + 20(13.33) + 50 = 177.77 + 266.67 + 50 = 494.44. Profit = 977.77 - 494.44 = Rs. 483.33.
      </p>

      <h3>Problem 2: Deadweight Loss Calculation</h3>
      <p>
        <strong>Q:</strong> From Problem 1, calculate the Deadweight Loss.<br/>
        <strong>Solution:</strong><br/>
        1. Find Competitive Equilibrium (P = MC).<br/>
        100 - 2Q = 2Q + 20  =&gt;  80 = 4Q  =&gt;  Qc = 20. Pc = 100 - 40 = Rs. 60.<br/>
        2. Identify Monopoly variables: Qm = 13.33, Pm = 73.33. MC at Qm = 2(13.33) + 20 = 46.67.<br/>
        3. Calculate DWL Area = 0.5 * Base * Height.<br/>
        Base (Quantity difference) = Qc - Qm = 20 - 13.33 = 6.67.<br/>
        Height (Price minus MC at Qm) = Pm - MC(Qm) = 73.33 - 46.67 = 26.66.<br/>
        DWL = 0.5 * 6.67 * 26.66 = <strong>88.91</strong>.
      </p>

      <h3>Problem 3: Lerner Index</h3>
      <p>
        <strong>Q:</strong> A monopolist has MC = Rs. 40. Demand elasticity is -3. What price should they charge? What is the Lerner Index?<br/>
        <strong>Solution:</strong><br/>
        1. Use formula: MR = P(1 - 1/|e|).<br/>
        2. MR = MC, so 40 = P(1 - 1/3) = P(2/3).<br/>
        3. P = 40 * (3/2) = <strong>Rs. 60</strong>.<br/>
        4. Lerner Index = (P - MC)/P = (60 - 40)/60 = 20/60 = <strong>0.33</strong> (or 1/|e| = 1/3 = 0.33).
      </p>

      <h3>Problem 4: Third-Degree Price Discrimination</h3>
      <p>
        <strong>Q:</strong> A monopolist sells in Market 1 (P1 = 50 - Q1) and Market 2 (P2 = 90 - 2Q2). Total Cost is TC = 10Q + 20, where Q = Q1 + Q2. Find Q1, P1, Q2, P2.<br/>
        <strong>Solution:</strong><br/>
        1. Find MR1 and MR2: MR1 = 50 - 2Q1. MR2 = 90 - 4Q2.<br/>
        2. Find MC: MC = 10.<br/>
        3. Set MR1 = MC: 50 - 2Q1 = 10 =&gt; 40 = 2Q1 =&gt; <strong>Q1 = 20</strong>.<br/>
        P1 = 50 - 20 = <strong>Rs. 30</strong>.<br/>
        4. Set MR2 = MC: 90 - 4Q2 = 10 =&gt; 80 = 4Q2 =&gt; <strong>Q2 = 20</strong>.<br/>
        P2 = 90 - 2(20) = <strong>Rs. 50</strong>.
      </p>

      <h3>Problem 5: Elasticity and Price Discrimination</h3>
      <p>
        <strong>Q:</strong> From Problem 4, calculate elasticity in both markets and verify the rule that the market with lower elasticity gets the higher price.<br/>
        <strong>Solution:</strong><br/>
        e1 = (dQ1/dP1) * (P1/Q1). Since P1 = 50 - Q1, Q1 = 50 - P1, so dQ1/dP1 = -1. <br/>
        |e1| = |-1| * (30/20) = <strong>1.5</strong>.<br/>
        e2 = (dQ2/dP2) * (P2/Q2). Since P2 = 90 - 2Q2, Q2 = 45 - 0.5P2, so dQ2/dP2 = -0.5.<br/>
        |e2| = |-0.5| * (50/20) = <strong>1.25</strong>.<br/>
        Since 1.25 &lt; 1.5, Market 2 has less elastic demand, which perfectly aligns with the firm charging a higher price (Rs. 50 &gt; Rs. 30) in Market 2.
      </p>

      <h3>Problem 6: Multi-plant Monopoly</h3>
      <p>
        <strong>Q:</strong> A monopolist produces a single product in two plants. Demand: P = 120 - 3Q (where Q = Q1 + Q2). Cost Plant 1: C1 = 10Q1 + 2Q1^2. Cost Plant 2: C2 = 60Q2 + Q2^2. Find optimal output in each plant.<br/>
        <strong>Solution:</strong><br/>
        Rule: MR = MC1 = MC2.<br/>
        1. MR = 120 - 6Q = 120 - 6(Q1 + Q2).<br/>
        2. MC1 = 10 + 4Q1.<br/>
        3. MC2 = 60 + 2Q2.<br/>
        Set MC1 = MC2: 10 + 4Q1 = 60 + 2Q2 =&gt; 2Q2 = 4Q1 - 50 =&gt; Q2 = 2Q1 - 25.<br/>
        Set MR = MC1: 120 - 6Q1 - 6(2Q1 - 25) = 10 + 4Q1<br/>
        120 - 6Q1 - 12Q1 + 150 = 10 + 4Q1<br/>
        270 - 18Q1 = 10 + 4Q1<br/>
        260 = 22Q1 =&gt; <strong>Q1 = 11.82</strong>.<br/>
        Q2 = 2(11.82) - 25 = 23.64 - 25 = -1.36.<br/>
        <em>Crucial observation:</em> Quantity cannot be negative! The firm will shut down Plant 2 and produce everything in Plant 1. Therefore, Q2 = 0.<br/>
        Recalculate with only Plant 1: 120 - 6Q1 = 10 + 4Q1 =&gt; 110 = 10Q1 =&gt; <strong>Q1 = 11, Q2 = 0.</strong>
      </p>

      <h3>Problem 7: First Degree Price Discrimination</h3>
      <p>
        <strong>Q:</strong> A monopolist with Demand P = 100 - Q and MC = 20 practices perfect (1st degree) price discrimination. Find their total profit.<br/>
        <strong>Solution:</strong><br/>
        1. Output is where P = MC: 100 - Q = 20 =&gt; Q = 80.<br/>
        2. Total Revenue is the entire area under the demand curve up to Q=80.<br/>
        TR = Area of trapezoid = 0.5 * (100 + 20) * 80 = 0.5 * 120 * 80 = 4,800.<br/>
        3. Total Variable Cost (since MC is constant) = MC * Q = 20 * 80 = 1,600.<br/>
        4. Profit (ignoring fixed cost) = 4,800 - 1,600 = <strong>3,200</strong>.<br/>
        Notice this equals the entire Consumer Surplus + Producer Surplus of a competitive market.
      </p>

      <h3>Problem 8: Natural Monopoly Regulation</h3>
      <p>
        <strong>Q:</strong> Natural Monopoly faces Demand P = 50 - Q. TC = 100 + 10Q. Compare MC pricing and ATC pricing.<br/>
        <strong>Solution:</strong><br/>
        MC = 10. ATC = 100/Q + 10.<br/>
        <em>Case 1: MC Pricing (Allocative Efficiency)</em><br/>
        Set P = MC: 50 - Q = 10 =&gt; Q = 40. P = 10.<br/>
        Profit = TR - TC = (10*40) - (100 + 10(40)) = 400 - 500 = <strong>-100</strong>.<br/>
        The firm loses 100 and requires a government subsidy.<br/>
        <em>Case 2: Average Cost Pricing (Fair Rate of Return)</em><br/>
        Set P = ATC: 50 - Q = 100/Q + 10<br/>
        40 - Q = 100/Q =&gt; 40Q - Q^2 = 100 =&gt; Q^2 - 40Q + 100 = 0.<br/>
        Using quadratic formula: Q = [40 +/- sqrt(1600 - 400)] / 2 = [40 +/- 34.64]/2.<br/>
        Q = 37.32 (since we want the larger output for regulation).<br/>
        P = 50 - 37.32 = <strong>12.68</strong>.<br/>
        Profit = TR - TC = (12.68 * 37.32) - (100 + 10*37.32) = 473 - 473 = <strong>0</strong>.<br/>
        Firm breaks even, no subsidy required, but output is slightly less than efficient Q=40.
      </p>

      <hr />

      <h2>9. DU Exam Blueprint &amp; Model 15-Mark Solutions</h2>
      
      <h3>How Unit 6 is Tested</h3>
      <p>
        Unit 6 generally accounts for 20-25% of your final exam weight. You will almost certainly see a 15-mark composite question structured like this:
      </p>
      <ul>
        <li><strong>Part A (5 Marks):</strong> Theory/Proof. "Prove that a monopolist will never operate on the inelastic portion of the demand curve" or "Explain the concept of Deadweight Loss using a diagram."</li>
        <li><strong>Part B (10 Marks):</strong> Numerical. Usually Third-Degree Price Discrimination across two markets, asking you to calculate P, Q, and Profit under discrimination vs. non-discrimination, and prove the elasticities.</li>
      </ul>

      <h3>Exam Tips to Maximize Marks</h3>
      <ol>
        <li><strong>Always Draw the Diagram:</strong> In any theory question about monopoly equilibrium, DWL, or price discrimination, a well-labeled graph is worth 2-3 explicit marks. Label axes (Price/Cost vs Quantity), mark MR, AR, MC, and clearly shade the DWL or Profit boxes.</li>
        <li><strong>Second Order Conditions:</strong> In numerical problems, after finding MR = MC, add a quick line: <em>"Check SOC: Slope of MR (-4) &lt; Slope of MC (2). Condition satisfied."</em> Examiners look for this explicitly for the final out-of-10 mark.</li>
        <li><strong>Formulas First:</strong> Don't just crunch numbers. Write MR = P(1 - 1/|e|) before plugging in values. State the profit-maximizing rule clearly before solving equations.</li>
        <li><strong>Check for Corner Solutions:</strong> As seen in Problem 6, if your math yields a negative quantity for a market or plant, state that output cannot be negative and set it to zero, recalculating for the active market.</li>
      </ol>

      <p>
        Mastering monopoly and price discrimination gives you the algebraic and geometric intuition needed for everything that follows in intermediate microeconomics, especially oligopoly and game theory. Drill the numericals until the MR=MC rule becomes second nature. Good luck with your preparation!
      </p>
    </>
  );
}


